/**
 * The result of a spoken attempt that just ended, shown where it was held.
 *
 * Driven by the spoken-attempt finalizer (realtime/spokenAttemptFinalize): while
 * finalize runs it says so; when it is done it shows the persisted result (and
 * the NPC level outcome, for a level attempt); when it was refused it says why,
 * from the one attempt-error map, with "Try again" only when retrying can help.
 * Nothing is ever shown as a score that the server did not measure.
 */

import type { ConversationResult } from '../store/api/coachingApi';
import { useGetCoachingResultQuery } from '../store/api/coachingApi';
import type { SpokenFinalizeState } from '../realtime/spokenAttemptFinalize';
import { ConversationResults } from './ConversationResults';
import { LevelOutcomeCard } from './flows/FlowOutcomes';
import { LoadErrorNotice } from './LoadErrorNotice';

export interface SpokenAttemptOutcomeViewProps {
  state: SpokenFinalizeState;
  /** The persisted result, once finalized and read. */
  result?: ConversationResult | undefined;
  resultFailed?: boolean;
  resultError?: unknown;
  onReloadResult?: () => void;
  onRetryFinalize?: () => void;
  retrying?: boolean;
  levelName?: string;
}

export function SpokenAttemptOutcomeView({
  state, result, resultFailed, resultError, onReloadResult, onRetryFinalize, retrying, levelName,
}: SpokenAttemptOutcomeViewProps) {
  if (state.phase === 'finalizing') {
    return (
      <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700" role="status" data-testid="spoken-outcome-finalizing">
        Scoring your conversation…
      </p>
    );
  }

  if (state.phase === 'refused') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" role="alert" data-testid="spoken-outcome-refused">
        <p className="text-sm text-amber-900">{state.error.message}</p>
        {state.error.retryable && onRetryFinalize && (
          <button
            type="button"
            onClick={onRetryFinalize}
            disabled={retrying}
            className="mt-2 text-sm font-medium text-amber-900 underline disabled:opacity-50"
          >
            {retrying ? 'Trying again…' : 'Try again'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="spoken-outcome-finalized">
      {state.note && <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{state.note}</p>}
      {state.level && <LevelOutcomeCard outcome={state.level} {...(levelName ? { levelName } : {})} />}
      {result ? (
        <ConversationResults result={result} />
      ) : resultFailed ? (
        <LoadErrorNotice
          error={resultError}
          onRetry={() => onReloadResult?.()}
          failedMessage="Your results could not be loaded."
          testIdPrefix="spoken-result"
        />
      ) : (
        <p className="text-sm text-gray-500">Loading your results…</p>
      )}
    </div>
  );
}

/** The view, reading the persisted result once the attempt is finalized. */
export function SpokenAttemptOutcome(props: Omit<SpokenAttemptOutcomeViewProps, 'result' | 'resultFailed' | 'resultError' | 'onReloadResult'>) {
  const finalized = props.state.phase === 'finalized';
  // Read only AFTER finalize: an earlier read would cache "not evaluated".
  const { data, isError, error, refetch } = useGetCoachingResultQuery(props.state.sessionId, { skip: !finalized });
  return (
    <SpokenAttemptOutcomeView
      {...props}
      result={finalized ? data : undefined}
      resultFailed={isError}
      resultError={error}
      onReloadResult={() => void refetch()}
    />
  );
}

export default SpokenAttemptOutcome;
